import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch';
import { AlertCircle, Award, Bell, BellOff, Check, Copy, Edit, Edit2, Image, Info, Pen, Plus, RefreshCw, ShieldCheck, Trash} from 'lucide-react';
import {
    getActiveSubscription,
    getNotificationPermission,
    getPushSubscriptionKeys,
    isWebPushSupported,
    refreshAgentWebPushSubscription,
    refreshBrowserPushSubscription,
    registerAgentWebPushSubscription,
    subscribeBrowserToPush,
    unsubscribeBrowserFromPush,
} from '@/lib/webPush';
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import api from '@/lib/axiosInstance';
import Helper from '@/lib/helper';
import type { Agents } from '@/types/Agents';
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSeparator,
    InputOTPSlot,
  } from "@/components/ui/input-otp"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { States } from '@/types/States';
import type { Cities } from '@/types/Cities';
import type { AgentExpertise } from '@/types/AgentExpertise';
import { Checkbox } from '@/components/ui/checkbox';
import LabelWithAsterisk from '@/components/LabelWithAsterisk';
import CertificateView, { type CertificateTemplate } from '@/components/CertificateView';

/** Kept in step with AgentController.AGENT_VIDEO_MAX_DURATION on the API. */
const MAX_PROFILE_VIDEO_SECONDS = 30;

const Profile = () => {
    const [agent, setAgent] = useState<Agents | null>(null);
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [contact, setContact] = useState<string>('');
    const [nric, setNric] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [open, setOpen] = useState<boolean>(false);
    const [gettingCode, setGettingCode] = useState<boolean>(false);
    const [verifyingCode, setVerifyingCode] = useState<boolean>(false);
    const [codeCountDown, setCodeCountDown] = useState<number | null>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const [code, setCode] = useState<string>('');
    const [states, setStates] = useState<States[] | null>(null);
    const [cities, setCities] = useState<Cities[] | null>(null);
    const [areaOfExpertise, setAreaOfExpertise] = useState<({
        stateId: number | null,
        cityId: number | null
    } | AgentExpertise)[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const [hobbies, setHobbies] = useState<any>();
    const [languages, setLanguages] = useState<any>();
    const [selectedHobbies, setSelectedHobbies] = useState<number[]>([]);
    const [selectedLanguages, setSelectedLanguages] = useState<number[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isUploadingAvatar, setIsUploadingAvatar] = useState<boolean>(false);
    const [isUploadingVideo, setIsUploadingVideo] = useState<boolean>(false);
    const [isSavingVideoUsage, setIsSavingVideoUsage] = useState<boolean>(false);
    const videoInputRef = useRef<HTMLInputElement>(null);
    const [certificate, setCertificate] = useState<{ earned: boolean; template: CertificateTemplate | null }>({ earned: false, template: null });
    const [currentRank, setCurrentRank] = useState<{ name?: string; medal?: string | null; color?: string | null } | null>(null);
    const [certificateOpen, setCertificateOpen] = useState<boolean>(false);
    const [banks, setBanks] = useState<{id: number, name: string}[]>([]);
    const [bankId, setBankId] = useState<number | null>(agent?.bankId ?? null);
    const [accountNumber, setAccountNumber] = useState<string>(agent?.accountNumber ?? '');
    const pushSupported = isWebPushSupported();
    const [pushEnabled, setPushEnabled] = useState<boolean>(false);
    const [pushPermission, setPushPermission] = useState<NotificationPermission | 'unsupported'>(pushSupported ? getNotificationPermission() : 'unsupported');
    const [pushBusy, setPushBusy] = useState<boolean>(false);
    const [pushVapidKey, setPushVapidKey] = useState<string>('');

    const getSiteSettingsUrl = () => {
        if (typeof window === 'undefined') return '#';
        const ua = navigator.userAgent.toLowerCase();
        if (ua.includes('edg/')) return 'edge://settings/content/notifications';
        if (ua.includes('firefox/')) return 'about:preferences#privacy';
        return 'chrome://settings/content/notifications';
    };

    const copySiteSettingsUrl = async () => {
        const url = getSiteSettingsUrl();
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Browser notification settings link copied.');
        } catch {
            toast.error(`Copy this link manually: ${url}`);
        }
    };

    const saveBrowserPushSubscription = async (sub: PushSubscription, permission: NotificationPermission) => {
        const json = sub.toJSON();
        json.keys = { ...json.keys, ...getPushSubscriptionKeys(sub) };
        const response = await api.post('/me/web-push/subscriptions', {
            subscription: json,
            userAgent: navigator.userAgent,
            permission,
        });
        if (!response.data.status) throw new Error(response.data.message || response.data.error);
    };

    const loadPushState = async () => {
        if (!pushSupported) {
            setPushPermission('unsupported');
            setPushEnabled(false);
            return;
        }

        const permission = getNotificationPermission();
        setPushPermission(permission);
        if (permission !== 'granted') {
            setPushEnabled(false);
            return;
        }

        try {
            const response = await api.get('/me/push-subscription');
            let vapidKey = pushVapidKey;
            if (response.data.status) {
                vapidKey = response.data.publicKey || response.data.vapidPublicKey || '';
                setPushVapidKey(vapidKey);
                const sub = await getActiveSubscription();
                if (sub) {
                    const matched = response.data.subscriptions?.some((s: any) => s.endpoint === sub.endpoint);
                    if (!matched) await saveBrowserPushSubscription(sub, permission);
                    setPushEnabled(true);
                    return;
                }

                if (vapidKey) {
                    const newSub = await subscribeBrowserToPush(vapidKey);
                    if (newSub) {
                        await saveBrowserPushSubscription(newSub, permission);
                        setPushEnabled(true);
                        return;
                    }
                }
            }
            setPushEnabled(false);
        } catch {
            setPushEnabled(false);
        }
    };

    const togglePushNotifications = async (next: boolean) => {
        if (!pushSupported) return;
        setPushBusy(true);
        try {
            if (next) {
                const permission = await Notification.requestPermission();
                setPushPermission(permission);
                if (permission !== 'granted') {
                    toast.error('Notifications were blocked. Enable them in your browser settings.');
                    return;
                }
                let vapidKey = pushVapidKey;
                if (!vapidKey) {
                    const keyResponse = await api.get('/me/web-push/public-key');
                    if (!keyResponse.data.status) throw new Error(keyResponse.data.message || keyResponse.data.error);
                    vapidKey = keyResponse.data.publicKey || '';
                    setPushVapidKey(vapidKey);
                }
                const result = await registerAgentWebPushSubscription();
                if (!result.status) throw new Error(result.reason || 'Failed to create push subscription.');
                setPushEnabled(true);
                toast.success('Web push notifications enabled.');
            } else {
                const endpoint = await unsubscribeBrowserFromPush();
                if (endpoint) {
                    await api.delete(`/me/push-subscription?endpoint=${encodeURIComponent(endpoint)}`);
                } else {
                    await api.delete('/me/push-subscription');
                }
                setPushEnabled(false);
                toast.success('Web push notifications disabled.');
            }
        } catch (err: any) {
            toast.error(Helper.errorHandler(err));
        } finally {
            setPushBusy(false);
        }
    };

    const refreshPushNotifications = async () => {
        if (!pushSupported) return;
        setPushBusy(true);
        try {
            const permission = getNotificationPermission();
            setPushPermission(permission);
            if (permission !== 'granted') {
                toast.error('Notifications are not enabled for this browser.');
                return;
            }

            const result = await refreshAgentWebPushSubscription();
            if (!result.status) throw new Error(result.reason || 'Failed to refresh push subscription.');
            setPushEnabled(true);
            toast.success('Web push subscription refreshed.');
        } catch (err: any) {
            toast.error(Helper.errorHandler(err));
        } finally {
            setPushBusy(false);
        }
    };

    const handleOTPChange = (value: string) => {
        const sanitized = value.replace(/\D/g, "").slice(0, 6);
        setCode(sanitized);
    };

    const getBanks = async () => {
        try {
            const response = await api.get(`/banks`);
            if(!response.data.status) throw new Error(response.data.error);
            setBanks(response.data.banks);
        } catch(err: any) {
            toast.error(Helper.errorHandler(err));
        }
    }

    const getMe = async () => {
        try {
            setIsLoading(true);
            const response = await api.get(`/me`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setAgent(response.data.agent);
            setHobbies(response.data.hobbies);
            setLanguages(response.data.languages);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    const getCertificate = async () => {
        try {
            const response = await api.get('/agent-handbook/certificate');
            if (!response.data.status) return;
            setCertificate(response.data.certificate || { earned: false, template: null });
            setCurrentRank(response.data.currentRank || null);
        } catch {
            // Non-blocking: the certificate badge simply won't show.
        }
    }

    const removeError = (field: string) => {
        setValidationErrors(prevErrors => prevErrors.filter(x => x !== field));
    };

    const rules = [
        { label: 'At least 1 uppercase letter', regex: /[A-Z]/ },
        { label: 'At least 1 lowercase letter', regex: /[a-z]/ },
        { label: 'At least 1 number', regex: /[0-9]/ },
        { label: 'At least 1 special character', regex: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/ },
        { label: 'Minimum 8 characters', regex: /.{8,}/ },
    ];

    const getSMSCode = async () => {
        try {
            setGettingCode(true);
            const response = await api.get(`/me/code`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setCode('');
            setCodeCountDown(response.data.countdown);
        } catch (err : any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setGettingCode(false);
        }
    }

    const verifySMSCode = async (verificationCode: string) => {
        try {
            setVerifyingCode(true);
            const response = await api.post(`/me/code`, { code: verificationCode });
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setAgent(response.data.agent);
            setOpen(false);
            setCode('');
            setCodeCountDown(null);
            toast.success('Phone number verified.');
        } catch (err : any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
            setCode('');
        } finally {
            setVerifyingCode(false);
        }
    }

    useEffect(() => {
        if (code.length === 6 && codeCountDown && !verifyingCode) {
            verifySMSCode(code);
        }
    }, [code, codeCountDown, verifyingCode]);

    const checkRule = (rule: { label: string; regex: RegExp }) => rule.regex.test(password);

    const validatePassword = (pwd: string) => {
        const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/;
        return regex.test(pwd);
    };

    const uploadProfilePicture = async (e: any) => {
        try {
            setIsUploadingAvatar(true);
            const file = e.target.files?.[0];
            if (!file) throw new Error('Something went wrong.');
        
            const formData = new FormData();
            formData.append('avatar', file); 
            const response = await api.post(`/me/avatar`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setAgent(response.data.agent);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setIsUploadingAvatar(false);
            e.target.value = '';
        }
    }

    const readVideoDuration = (file: File): Promise<number> => {
        return new Promise((resolve, reject) => {
            const element = document.createElement('video');
            element.preload = 'metadata';
            element.onloadedmetadata = () => {
                URL.revokeObjectURL(element.src);
                resolve(element.duration);
            };
            element.onerror = () => {
                URL.revokeObjectURL(element.src);
                reject(new Error('Could not read that video file.'));
            };
            element.src = URL.createObjectURL(file);
        });
    };

    const uploadProfileVideo = async (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            setIsUploadingVideo(true);
            const file = e.target.files?.[0];
            if (!file) throw new Error('Something went wrong.');
            if (file.type !== 'video/mp4') throw new Error('Only MP4 videos are allowed.');

            // Checked here so the agent finds out before a long upload; the API
            // enforces the same limit with ffprobe.
            const duration = await readVideoDuration(file);
            if (duration > MAX_PROFILE_VIDEO_SECONDS) {
                throw new Error(`Video is ${Math.round(duration)}s long. Maximum is ${MAX_PROFILE_VIDEO_SECONDS}s.`);
            }

            const formData = new FormData();
            formData.append('video', file);
            const response = await api.post('/me/video', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            if (!response.data.status) {
                throw new Error(response.data.error);
            }
            setAgent(response.data.agent);
            toast.success('Intro video uploaded.');
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setIsUploadingVideo(false);
            e.target.value = '';
        }
    };

    const removeProfileVideo = async () => {
        try {
            setIsUploadingVideo(true);
            const response = await api.delete('/me/video');
            if (!response.data.status) {
                throw new Error(response.data.error);
            }
            setAgent(response.data.agent);
            toast.success('Intro video removed.');
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setIsUploadingVideo(false);
        }
    };

    const toggleProfileVideoUsage = async (useInLive: boolean) => {
        try {
            setIsSavingVideoUsage(true);
            const response = await api.post('/me/video/use-in-live', { useInLive }, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!response.data.status) {
                throw new Error(response.data.error);
            }
            setAgent(response.data.agent);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setIsSavingVideoUsage(false);
        }
    };

    const updatePassword = async () => {
        try {
            if (!validatePassword(password)) {
                throw new Error('Password does not fulfill the requirements.');
            }
    
            if(password !== confirmPassword) {
                throw new Error('Password does not fulfill the requirements.')
            }

            var formdata = new FormData();
            formdata.append('password', password);
            formdata.append('confirmPassword', confirmPassword);

            const response = await api.post(`/me/password`, formdata);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            toast.success('Successfully updated password!');
        } catch (err : any) {
            if (!validationErrors.includes('password')) {
                validationErrors.push('password');
                setValidationErrors([...validationErrors]);
            }
            toast.error(err.message);
        }
    };

    const updateProfile = async () => {
        try {
            const errors: string[] = [];

            if (firstName !== undefined) {
                if (!firstName.trim() || firstName.trim().length < 2) {
                    errors.push('firstName');
                }
            }

            if (lastName !== undefined) {
                if (!lastName.trim() || lastName.trim().length < 2) {
                    errors.push('lastName');
                }
            }

            if (contact !== undefined) {
                if(!contact) {
                    errors.push('contact');
                } else {
                    if (!/^(01[0-9]{8,9}|601[0-9]{7,9})$/.test(contact)) {
                        errors.push('contact-format');
                    }
                }
            }
            if (!description.trim()) errors.push('description');
            if (!bankId) errors.push('bankId');
            if (!accountNumber.trim()) errors.push('accountNumber');
            if (!nric.trim()) errors.push('nric');

            if(errors.length > 0) {
                setValidationErrors(errors);
                throw new Error('Please complete all required fields.');
            }

            var formdata = new FormData();
            formdata.append('firstName', firstName);
            formdata.append('lastName', lastName);
            formdata.append('contact', contact);
            formdata.append('bankId', String(bankId));
            formdata.append('accountNumber', accountNumber);
            formdata.append('description', description);
            formdata.append('nric', nric)

            const response = await api.post(`/me/profiles`, formdata);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setAgent(response.data.agent);
            toast.success('Successfully updated profile!');
        } catch (err : any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    }

    const getCityAndState = async () => {
        try {
            const response = await api.get(`/listings/addresses/criteria`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setStates(response.data.states);
            setCities(response.data.cities);
        } catch (err : any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    }

    const uploadPortfolio = async (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            if (!agent) return; 
            const files = e.target.files;
            if (!files || files.length === 0) return;
        
            const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
            if (imageFiles.length === 0) {
                throw new Error('Invalid file type. Only .png, .jpg, .jpeg is allowed.');
            }
        
            const formdata = new FormData();
            imageFiles.forEach((file) => {
                formdata.append('portfolios', file);
            });
        
            const response = await api.post(`/me/portfolios`, formdata);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setAgent((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    portfolios: [...(prev.portfolios || []), ...(response.data.portfolios || [])],
                } as Agents;
            });
        } catch (err : any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            e.target.value = '';
        }
    };

    const removePortfolio = async (portfolioId: number) => {
        try {
            if (!portfolioId) {
                throw new Error("Invalid ID.");
            }
        
            const response = await api.delete(`/me/portfolios/${portfolioId}`);
            if (!response.data.status) {
                throw new Error(response.data.error);
            }
        
            if (agent) {
                setAgent({
                    ...agent,
                    portfolios: agent.portfolios.filter(p => p.id !== portfolioId),
                } as Agents);
            }
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    };

    const saveExpertise = async (index: number) => {
        try {
            if (!Array.isArray(areaOfExpertise)) return;
            const expertise = areaOfExpertise[index];
            if (!expertise) return;
        
            const formdata = new FormData();
            formdata.append('stateId', String(expertise.stateId));
            formdata.append('cityId', String(expertise.cityId));
        
            const response = await api.post(`/me/expertise`, formdata);
            if (!response.data.status) {
                throw new Error(response.data.error);
            }
            const updatedExpertise = [...areaOfExpertise];
            updatedExpertise[index] = {
                ...expertise,
                ...response.data.expertise,
            };
            setAreaOfExpertise(updatedExpertise);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    };

    const updateExpertise = async (expertiseId: number) => {
        try {
            if (!Array.isArray(areaOfExpertise)) return;
        
            const index = areaOfExpertise.findIndex(e => e.id === expertiseId);
            if (index === -1) return;
        
            const expertise = areaOfExpertise[index];
            if (!expertise) return;
        
            const formdata = new FormData();
            formdata.append('stateId', String(expertise.stateId));
            formdata.append('cityId', String(expertise.cityId));
        
            const response = await api.post(`/me/expertise/${expertiseId}`, formdata);
        
            if (!response.data.status) {
                throw new Error(response.data.error);
            }
        
            const updatedExpertise = [...areaOfExpertise];
            updatedExpertise[index] = {
                ...expertise,
                ...response.data.expertise,
                edit: false, 
            };
            setAreaOfExpertise(updatedExpertise);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    };

    const toggleHobby = async (id: number) => {
        try {
            const updated = selectedHobbies.includes(id)
                ? selectedHobbies.filter(x => x !== id)
                : [...selectedHobbies, id];

            setSelectedHobbies(updated);
            const response = await api.post(`/me/hobbies`, 
                {
                    hobbies: updated
                }, 
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
            if(!response.data.status) throw new Error(response.data.error);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
            if (err) {
                getMe();
            }
        }
    };

    const toggleLanguage = async (id: number) => {
        try {
            const updated = selectedLanguages.includes(id)
                ? selectedLanguages.filter(x => x !== id)
                : [...selectedLanguages, id];

            setSelectedLanguages(updated);

            const response = await api.post(`/me/languages`, {
                languages: updated
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if(!response.data.status) throw new Error(response.data.error);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
            // Revert on error
            if (err) {
                getMe();
            }
        }
    };

    const deleteExpertise = async (expertiseId : number) => {
        try {
            const response = await api.delete(`/me/expertise/${expertiseId}`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setAreaOfExpertise((prev) =>
                prev.filter((item) => item.id !== expertiseId)
            );
        } catch (err : any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    }

    useEffect(() => {
        getMe();
        getCertificate();
        getCityAndState();
        getBanks();
        loadPushState();
    }, []);

    useEffect(() => {
        if(agent) {
            setFirstName(agent.firstName || '');
            setLastName(agent.lastName || '');
            setContact(agent.contact || '');
            setNric(agent.nric || '');
            setDescription(agent.description || '');
            setBankId(agent.bankId ?? null);
            setAccountNumber(agent.accountNumber ?? '');
            setAreaOfExpertise(agent.expertise ?? []);
            setSelectedHobbies(agent.hobbies ? JSON.parse(agent.hobbies) : []);
            setSelectedLanguages(agent.languages ? JSON.parse(agent.languages) : []);
        }
    }, [agent]);

    if (isLoading) {
        return (
            <div>
                <div className="mb-5">
                    <Skeleton className="h-8 w-32" />
                </div>
                
                <div className='flex items-center'>
                    <Skeleton className="h-[80px] w-[80px] rounded-full" />
                    <div className='ml-3 space-y-2'>
                        <Skeleton className="h-5 w-48" />
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-4 w-36" />
                        <Skeleton className="h-6 w-28" />
                    </div>
                </div>

                <hr className="mt-5" />
                <div className='mt-3'>
                    <Skeleton className="h-6 w-24 mb-3" />
                    <Skeleton className="h-20 w-full" />
                </div>

                <hr className="mt-5" />
                <div className='mt-3'>
                    <Skeleton className="h-6 w-32 mb-3" />
                    <div className='flex flex-wrap gap-2'>
                        <Skeleton className="w-[150px] h-[150px] rounded-lg" />
                        <Skeleton className="w-[150px] h-[150px] rounded-lg" />
                    </div>
                </div>
            </div>
        );
    }

    return agent && (
        <div>
            <div className="mb-5">
                <h5 className="h5 font-bold">PROFILE</h5>
            </div>
            
            <div className='flex items-center'>
                <div className="h-[120px] w-[120px] rounded-full border flex justify-center items-center relative group cursor-pointer"
                style={
                    agent.avatar ? { 
                        background: `url(${import.meta.env.VITE_AWS_S3}/${agent.avatar})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center center'
                    } : {}
                 }>
                    {
                        !agent.avatar && (
                            <Image color="gray" className="z-0" />
                        )
                    }
                    {
                        isUploadingAvatar ? (
                            <div className="absolute top-0 left-0 h-full w-full flex justify-center items-center bg-white/80 rounded-full z-10">
                                <Spinner className="h-6 w-6" />
                            </div>
                        ) : (
                            <div className="absolute top-0 left-0 h-full w-full flex justify-center items-center bg-white/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 rounded-full">
                                <Edit size={12} className="text-gray-700" 
                                onClick={() => {
                                    imageInputRef.current?.click();
                                }}/>
                            </div>
                        )
                    }
                    <Input 
                    type='file' 
                    ref={imageInputRef} 
                    hidden 
                    accept='.png, .jpg, .jpeg' 
                    onChange={uploadProfilePicture}/>
                </div>
                <div className='ml-3'>
                    <div className='flex items-center gap-2'>
                        <h5 className='text-sm font-bold'>{agent.firstName} {agent.lastName}</h5>
                        {
                            currentRank?.name && (() => {
                                const rankColor = currentRank.color && /^#[0-9a-fA-F]{3,8}$/.test(currentRank.color) ? currentRank.color : '#6366f1';
                                return (
                                    <Badge variant={'outline'} className='gap-1 border bg-white' style={{ borderColor: rankColor, color: rankColor }}>
                                        <span className='h-2 w-2 rounded-full' style={{ backgroundColor: rankColor }} />
                                        {currentRank.medal && <span>{currentRank.medal}</span>}
                                        <span>{currentRank.name}</span>
                                    </Badge>
                                );
                            })()
                        }
                        {
                            certificate.earned && certificate.template && (
                                <button
                                type='button'
                                title='View certificate of completion'
                                onClick={() => setCertificateOpen(true)}
                                className='inline-flex items-center justify-center rounded-full p-1 text-emerald-600 hover:bg-emerald-50 cursor-pointer'>
                                    <Award className='h-5 w-5' />
                                </button>
                            )
                        }
                    </div>
                    <h6 className='text-xs'>{agent.renNo}</h6>
                    <h6 className='text-xs'>{agent.email}</h6>
                    <div className='flex'>
                        <h6 className='text-xs'>+{agent.contact}</h6>
                    </div>
                    {/* Verify-your-number badge removed per request.
                        agent.isContactVerified === 0 ? (
                            <Badge
                            variant={'outline'}
                            className='text-yellow-500 cursor-pointer'
                            onClick={() => {
                                setOpen(true);
                            }}>
                                <AlertCircle/>
                                <span>Verify your number</span>
                            </Badge>
                        ) :
                        <Badge variant={'outline'} className='bg-blue-300 border-0 text-white'>
                            <ShieldCheck className='text-white'/>
                            <span>Verified</span>
                        </Badge>
                    */}
                    
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Verify Your Number</DialogTitle>
                            <DialogDescription>
                            Please confirm your phone number to continue.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="mt-3 flex flex-col items-center">
                            <InputOTP maxLength={6} 
                            disabled={!codeCountDown}
                            value={code}
                            onChange={handleOTPChange}>
                                <InputOTPGroup>
                                    <InputOTPSlot index={0} />
                                    <InputOTPSlot index={1} />
                                    <InputOTPSlot index={2} />
                                </InputOTPGroup>
                                <InputOTPSeparator />
                                <InputOTPGroup>
                                    <InputOTPSlot index={3} />
                                    <InputOTPSlot index={4} />
                                    <InputOTPSlot index={5} />
                                </InputOTPGroup>
                            </InputOTP>
                            <Button 
                            className='mt-5' 
                            size={'sm'} 
                            disabled={gettingCode || verifyingCode}
                            onClick={getSMSCode}>
                                GET CODE
                                {
                                    (verifyingCode || gettingCode) && (
                                        <Spinner />
                                    )
                                }
                            </Button>
                        </div>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>
            <hr className="mt-5" />
            <h6 className="h6 font-bold mt-3">About Me</h6>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <LabelWithAsterisk label="First Name" className="mb-1" />
                    <Input
                    className={`text-sm ${validationErrors.includes("firstName") ? "border-red-400" : ""}`}
                    value={firstName}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (value) removeError("firstName");
                        setFirstName(value);
                    }}/>
                    {validationErrors.includes("firstName") && (
                        <span className="block text-xs text-red-400 mt-1">First name is required.</span>
                    )}
                </div>
                <div>
                    <LabelWithAsterisk label="Last Name" className="mb-1" />
                    <Input
                    className={`text-sm ${validationErrors.includes("lastName") ? "border-red-400" : ""}`}
                    value={lastName}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (value) removeError("lastName");
                        setLastName(value);
                    }}/>
                    {validationErrors.includes("lastName") && (
                        <span className="block text-xs text-red-400 mt-1">Last name is required.</span>
                    )}
                </div>
                <div>
                    <Label className="mb-1">Email</Label>
                    <Input className="w-full text-sm" value={agent.email} disabled />
                </div>
                <div>
                    <LabelWithAsterisk label="Contact" className="mb-1" />
                    <Input
                    className={`w-full text-sm ${
                        validationErrors.includes("contact") || validationErrors.includes("contact-format")
                        ? "border-red-400"
                        : ""
                    }`}
                    maxLength={12}
                    value={contact}
                    onKeyDown={Helper.allowOnlyNumbers}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (value) {
                        removeError("contact");
                        removeError("contact-format");
                        }
                        setContact(value);
                    }}/>
                    {validationErrors.includes("contact") && (
                        <span className="block text-xs text-red-400 mt-1">Contact is required.</span>
                    )}
                    {validationErrors.includes("contact-format") && (
                        <span className="block text-xs text-red-400 mt-1">
                            Contact must start with 01 or 601 and be 10–11 digits long.
                        </span>
                    )}
                </div>
                <div>
                    <LabelWithAsterisk label="NRIC" className="mb-1" />
                    <Input
                        className={`w-full text-sm ${
                            validationErrors.includes("nric") ? "border-red-400" : ""
                        }`}
                        value={nric}
                        maxLength={12}
                        placeholder="e.g. 900101-01-1234"
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value) removeError("nric");
                            setNric(value);
                        }}
                    />
                    {validationErrors.includes("nric") && (
                        <span className="block text-xs text-red-400 mt-1">
                            NRIC is required.
                        </span>
                    )}
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                    <LabelWithAsterisk label="Bank" className="mb-1" />
                    <Select value={bankId ? String(bankId) : undefined} onValueChange={(val) => setBankId(Number(val))}>
                        <SelectTrigger className="w-full text-sm">
                            <SelectValue placeholder="Select bank" />
                        </SelectTrigger>
                        <SelectContent>
                            {banks.map((bank) => (
                            <SelectItem key={bank.id} value={String(bank.id)}>
                                {bank.name}
                            </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {validationErrors.includes("bankId") && (
                        <span className="block text-xs text-red-400 mt-1">Bank is required.</span>
                    )}
                </div>
                <div>
                    <LabelWithAsterisk label="Account Number" className="mb-1" />
                    <Input
                    className={`text-sm ${validationErrors.includes("accountNumber") ? "border-red-400" : ""}`}
                    value={accountNumber}
                    onChange={(e) => {
                        setAccountNumber(e.target.value);
                        removeError("accountNumber");
                    }}
                    placeholder="Enter account number" />
                    {validationErrors.includes("accountNumber") && (
                        <span className="block text-xs text-red-400 mt-1">Account number is required.</span>
                    )}
                </div>
                <div className="md:col-span-2">
                    <Label>Description</Label>
                    <Textarea
                    className={`mt-1 w-full text-sm ${validationErrors.includes("description") ? "border-red-400" : ""}`}
                    placeholder="Describe yourself here..."
                    value={description}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (value) removeError("description");
                        setDescription(value);
                    }} />
                </div>
            </div>
            <div className="flex justify-end flex-col sm:flex-row sm:items-center gap-2 mt-5">
                <Button 
 
                size={'sm'}
                onClick={updateProfile}>Save</Button>
            </div>
            <hr className="mt-5" />
            <div className='mt-3'>
                <h6 className={`h6 font-bold mb-1`}>Intro video</h6>
                <p className='text-xs text-muted-foreground mb-5'>
                    A short MP4 introducing yourself, up to {MAX_PROFILE_VIDEO_SECONDS} seconds.
                    Turn on "Use on live listings" to show it in the agent section of your published listings.
                    A listing can override this with its own video.
                </p>
                <div className='flex flex-wrap items-start gap-4'>
                    {
                        agent.videoPath ? (
                            <div className='w-[260px]'>
                                <div className='relative rounded-lg overflow-hidden border bg-black'>
                                    {/* Keyed on the path: a <video> keeps the resource it
                                        already loaded when src changes, so replacing the
                                        clip has to remount the element to show the new one. */}
                                    <video
                                    key={agent.videoPath}
                                    src={`${import.meta.env.VITE_AWS_S3}/${agent.videoPath}`}
                                    className='w-full h-[150px] object-contain bg-black'
                                    controls
                                    playsInline
                                    preload='metadata'/>
                                    {
                                        isUploadingVideo && (
                                            <div className='absolute inset-0 flex justify-center items-center bg-white/70'>
                                                <Spinner className='h-6 w-6' />
                                            </div>
                                        )
                                    }
                                </div>
                                <div className='flex items-center justify-between gap-2 mt-2'>
                                    <span className='text-xs text-muted-foreground'>
                                        {
                                            agent.videoDurationSeconds
                                                ? `${Math.round(Number(agent.videoDurationSeconds))}s`
                                                : 'Uploaded'
                                        }
                                    </span>
                                    <div className='flex items-center gap-1'>
                                        <Button
                                        variant={'ghost'}
                                        size={'sm'}

                                        disabled={isUploadingVideo}
                                        onClick={() => videoInputRef.current?.click()}>
                                            <Pen size={14} />
                                        </Button>
                                        <Button
                                        variant={'ghost'}
                                        size={'sm'}

                                        disabled={isUploadingVideo}
                                        onClick={removeProfileVideo}>
                                            <Trash size={14} color='red' />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div
                            className='flex flex-col gap-2 justify-center items-center cursor-pointer border-1
                            border-gray-300 rounded-lg p-6 text-center hover:border-gray-500 transition w-[260px] h-[150px]'
                            onClick={() => {
                                if (!isUploadingVideo) videoInputRef.current?.click();
                            }}>
                                {
                                    isUploadingVideo ? (
                                        <Spinner className='h-6 w-6' />
                                    ) : (
                                        <>
                                            <Plus color='grey' />
                                            <span className='text-xs text-muted-foreground'>Upload MP4</span>
                                        </>
                                    )
                                }
                            </div>
                        )
                    }

                    <div className='flex items-center gap-3 pt-2'>
                        <Switch
                        id='video-use-in-live'
                        checked={agent.videoUseInLive === 1}
                        disabled={!agent.videoPath || isSavingVideoUsage || isUploadingVideo}
                        onCheckedChange={toggleProfileVideoUsage}
                        className='cursor-pointer'/>
                        <Label htmlFor='video-use-in-live' className='cursor-pointer'>
                            Use on live listings
                        </Label>
                    </div>

                    <input
                    type='file'
                    accept='video/mp4'
                    ref={videoInputRef}
                    className='hidden'
                    onChange={uploadProfileVideo}/>
                </div>
            </div>
            <hr className="mt-5" />
            <div className='mt-3'>
                <h6 className={`h6 font-bold mb-5`}>Portfolios</h6>
                <div className='flex flex-wrap gap-2'>
                    {
                        agent.portfolios?.length > 0 && agent.portfolios.map((file, index) => (
                            <div key={index} className="w-[150px] h-[150px] relative h-24 border rounded-lg overflow-hidden cursor-pointer">
                                <img
                                src={`${import.meta.env.VITE_AWS_S3}/${file.path}`}
                                className="w-full h-full object-cover"/>
                                <Button variant={'ghost'} 
                                size={'sm'} 
                                className='bg-transparent absolute top-0 right-0'
                                onClick={() => {
                                    removePortfolio(file.id);
                                }}>
                                    <Trash color='red'/>
                                </Button>
                            </div>
                        ))
                    }
                    <div
                    className="flex flex-wrap justify-center items-center cursor-pointer border-1 
                    border-gray-300 rounded-lg p-6 text-center cursor-pointer 
                    hover:border-gray-500 transition w-[150px] h-[150px]"
                    onClick={() => {
                        if(inputRef.current) {
                            inputRef.current.click();
                        }
                    }}>
                        <Plus color='grey'/>
                        <input
                        type="file"
                        multiple
                        accept="image/*"
                        ref={inputRef}
                        className="hidden"
                        onChange={uploadPortfolio}/>
                    </div>
                </div>
            </div>
            <hr className="mt-5" />
            <div className="mt-3">
                <h6 className={`h6 font-bold mb-5`}>
                    Language
                </h6>

                <div className="grid md:grid-cols-4 grid-cols-3 gap-3">
                    {
                        languages.map((lang) => (
                            <div key={lang.id} className="flex items-center space-x-2">
                                <Checkbox 
                                id={`lang-${lang.id}`}
                                checked={selectedLanguages.includes(lang.id)}
                                onCheckedChange={() => toggleLanguage(lang.id)}/>
                                <Label htmlFor={lang.id}>{lang.name}</Label>
                            </div>
                        ))
                    }
                </div>
            </div>
            <hr className="mt-5" />
            <div className="mt-3">
                <h6 className="h6 font-bold mb-5">Sports & Hobbies</h6>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {
                        hobbies.map((hobby) => (
                            <div key={hobby.id} className="flex items-center space-x-2">
                                <Checkbox 
                                id={`hobby-${hobby.id}`} 
                                checked={selectedHobbies.includes(hobby.id)}
                                onCheckedChange={() => toggleHobby(hobby.id)}/>
                                <Label
                                htmlFor={hobby.id}
                                className="flex items-center gap-2 cursor-pointer">
                                    {/* <Icon className="w-4 h-4 text-gray-500" /> */}
                                    <span>{hobby.name}</span>
                                </Label>
                            </div>
                        ))
                    }
                </div>
            </div>
            <hr className="mt-5" />
            <div className='mt-3'>
                <h6 className={`h6 font-bold ${areaOfExpertise.length > 0 && 'mb-5'}`}>Expertise</h6>
                <div>
                {
                    areaOfExpertise && areaOfExpertise.map((row, index) => (
                        <div key={index} className="flex items-center gap-2 mb-1">
                            <Select
                            value={row.stateId ? String(row.stateId) : undefined}
                            onValueChange={(val) => {
                                const newAreas = [...areaOfExpertise]
                                newAreas[index] = { ...row, stateId: Number(val), cityId: null }
                                setAreaOfExpertise(newAreas)
                            }} 
                            disabled={row.id ? !row.edit : false}>
                                <SelectTrigger className="w-[180px]">
                                    <SelectValue placeholder="Select state" />
                                </SelectTrigger>
                                <SelectContent>
                                    { 
                                        states && states.map((state) => (
                                            <SelectItem key={state.id} value={String(state.id)}>
                                                {state.name}
                                            </SelectItem>
                                        ))
                                    }
                                </SelectContent>
                            </Select>
                            <Select
                            value={row.cityId ? String(row.cityId) : undefined}
                            onValueChange={(val) => {
                                const newAreas = [...areaOfExpertise]
                                newAreas[index] = { ...row, cityId: Number(val) }
                                setAreaOfExpertise(newAreas)
                            }}
                            disabled={row.id ? !row.edit : !row.stateId}>
                                <SelectTrigger className="w-[180px]">
                                    <SelectValue placeholder="Select city" />
                                </SelectTrigger>
                                <SelectContent>
                                    {
                                        row.stateId && cities && cities
                                        .filter((c) => c.stateId === row.stateId)
                                        .map((city) => (
                                            <SelectItem key={city.id} value={String(city.id)}>
                                                {city.name}
                                            </SelectItem>
                                        ))
                                    }
                                </SelectContent>
                            </Select>
                            {
                                !row.id ? (
                                    <Button
                                    size="sm"

                                    onClick={() => {
                                        saveExpertise(index);
                                    }}>
                                        <Check />
                                    </Button>
                                ) : (
                                    <>
                                    {
                                        !row.edit ? 
                                        <Button
                                        size="sm"

                                        variant={'outline'}
                                        onClick={() => {
                                            if('id' in row) {
                                                setAreaOfExpertise(prev =>
                                                    prev.map(item =>
                                                        item.id === row.id ? { ...item, edit: true } : item
                                                    )
                                                );
                                            }
                                        }}>
                                            <Edit2 />
                                        </Button>
                                        :
                                        <Button
                                        size="sm"

                                        onClick={() => {
                                            if('id' in row) {
                                                updateExpertise(row.id);
                                            }
                                        }}>
                                            <Check />
                                        </Button>
                                    }
                                    </>
                                )
                            }
                            <Button
                            variant="destructive"
                            size="sm"

                            onClick={() => {
                                if('id' in row) {
                                    deleteExpertise(row.id)
                                } else {
                                    setAreaOfExpertise((prev) =>
                                        prev.filter((_, i) => i !== index)
                                    );
                                }
                            }}>
                                <Trash />
                            </Button>
                        </div>
                    ))
                }
                </div>
                <Button 
                className='mt-3' 
                size={'sm'}
                onClick={() => {
                    areaOfExpertise.push({
                        stateId: null,
                        cityId: null
                    })
                    setAreaOfExpertise([...areaOfExpertise]);
                }}>
                    <Plus/>
                    ADD
                </Button>
            </div>
            <hr className="mt-5" />
            <div className="mt-5 rounded-lg border bg-white p-4 md:w-1/2">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <div className="mt-0.5 rounded-md bg-grey-50 p-2 text-grey-600">
                            {pushEnabled ? <Bell size={18} /> : <BellOff size={18} />}
                        </div>
                        <div>
                            <h6 className="text-sm font-semibold">Web Push Notifications</h6>
                            <p className="mt-1 text-xs text-gray-500">
                                Receive alerts in this browser for new leads, claim updates, and other in-app activity.
                            </p>
                            <div className="mt-2 flex items-center gap-2 text-xs">
                                {pushPermission === 'unsupported' && (
                                    <Badge variant="outline" className="text-gray-500">Not supported in this browser</Badge>
                                )}
                                {pushPermission === 'granted' && (
                                    <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Permission granted</Badge>
                                )}
                                {pushPermission === 'denied' && (
                                    <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">Permission blocked</Badge>
                                )}
                                {pushPermission === 'default' && (
                                    <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">Permission not requested</Badge>
                                )}
                            </div>
                            {pushPermission === 'denied' && (
                                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                                    <span>Notifications are blocked at the browser level. Copy the settings URL, paste it in the address bar, then re-enable notifications.</span>
                                    <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={copySiteSettingsUrl}>
                                        <Copy className="mr-1 h-3 w-3" />
                                        Copy settings URL
                                    </Button>
                                </div>
                            )}
                            {pushEnabled && pushPermission === 'granted' && (
                                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                                    <span>Web push is enabled for this browser. To disable it, copy the settings URL, paste it in the address bar, then turn off notifications.</span>
                                    <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={refreshPushNotifications} disabled={pushBusy}>
                                        <RefreshCw className={`mr-1 h-3 w-3 ${pushBusy ? 'animate-spin' : ''}`} />
                                        Refresh subscription
                                    </Button>
                                    <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={copySiteSettingsUrl}>
                                        <Copy className="mr-1 h-3 w-3" />
                                        Copy settings URL
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <Switch
                            checked={pushEnabled}
                            disabled={!pushSupported || pushBusy || pushPermission === 'denied' || pushEnabled}
                            onCheckedChange={(next) => togglePushNotifications(next)}
                        />
                        {pushBusy && <span className="text-[10px] text-gray-400">Working…</span>}
                    </div>
                </div>
            </div>
            <hr className="mt-5" />
            <h6 className="h6 mt-3 font-bold">Change Your Password</h6>
            <div className="mt-5">
                <div className="md:w-1/2 w-full mb-3">
                    <Label className='text-gray-500'>New Password</Label>
                    <Input 
                    type="password" 
                    className={`mt-3 text-sm ${validationErrors.includes('password') && 'border-red-400'}`}
                    value={password} 
                    onChange={(e) => {
                        setPassword(e.target.value);
                        setConfirmPassword('');
                        removeError('password');
                    }}
                    placeholder="New password"/>
                </div>
                <div className="md:w-1/2 w-full mt-5">
                    <Label className='text-gray-500'>Confirm Password</Label>
                    <Input 
                    type="password" 
                    className={`mt-3 text-sm ${validationErrors.includes('password') && 'border-red-400'}`}
                    value={confirmPassword} 
                    onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        removeError('password');
                    }}
                    placeholder="Confirm password"/>
                </div>
            </div>
            <Alert className={`mt-5 ${validationErrors.includes('password') && 'border-red-400!'}`}>
                <AlertDescription >
                    <div className='flex items-center'>
                        <Info size={16}/>
                        <p className='ml-2'>The password requirements are as shown</p>
                    </div>
                    <div>
                        <ul className={`ml-3 list-disc text-sm text-gray-600 `}>
                            {
                                rules.map(rule => (
                                    <li 
                                    key={rule.label} 
                                    className={checkRule(rule) ? 'text-green-600' : 'text-gray-400'}>
                                        {rule.label}
                                    </li>
                                ))
                            }
                        </ul>
                    </div>
                </AlertDescription>
            </Alert>
            <div className='my-5 flex justify-end'>
                <Button size={'sm'} onClick={updatePassword}>SAVE</Button>
            </div>
            {
                certificate.template && (
                    <CertificateView
                    open={certificateOpen}
                    onOpenChange={setCertificateOpen}
                    template={certificate.template}
                    name={`${agent.firstName || ''} ${agent.lastName || ''}`.trim()}/>
                )
            }
        </div>
    );
}

export default Profile
